// /src/components/core/AnimatedSearchBar.jsx
// Animated search bar component - black circle that expands to reveal search input
// Click circle to expand, type to search (debounced 500ms), Enter to submit
// Close via: Escape key or clicking close button (when input is empty)
// Remains expanded when clicking search results - allows navigation without collapsing
// Mobile responsive: smaller circle, fills available width when expanded

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const AnimatedSearchBar = ({
  context = 'locality',
  onSearch,
  position = { right: '50px', top: '15px' },
  placeholder,
  zIndex = 10001
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { t } = useTranslation('ui');
  const { isMobile } = useMediaQuery();

  // Auto-focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 350);
    }
  }, [isExpanded]);

  // Escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Debounce utility
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((term) => {
      if (term.trim() && onSearch) {
        onSearch(term.trim());
      }
    }, 500),
    [onSearch]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      // Clear search immediately when input is empty
      if (onSearch) onSearch('');
    }
  };

  const handleSubmit = () => {
    if (searchTerm.trim() && onSearch) {
      onSearch(searchTerm.trim());
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setSearchTerm('');
    if (onSearch) onSearch(''); // Clear search
  };

  const handleExpand = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  // Sizing - consistent 40px circle on all devices
  const circleSize = '40px';
  const expandedWidth = isMobile ? '250px' : '300px';
  const iconSize = 20;
  const fontSize = isMobile ? '13px' : '14px';

  return (
    <div
      ref={containerRef}
      onClick={handleExpand}
      style={{
        width: isExpanded ? expandedWidth : circleSize,
        height: circleSize,
        borderRadius: isExpanded ? `calc(${circleSize} / 2)` : '50%',
        background: '#000000',
        position: 'absolute',
        right: position.right,
        top: position.top,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: isExpanded ? '20px' : '0',
        paddingRight: isExpanded ? '50px' : '0',
        justifyContent: isExpanded ? 'flex-start' : 'center',
        cursor: isExpanded ? 'default' : 'pointer',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: zIndex,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        pointerEvents: 'auto'
      }}
    >
      {!isExpanded ? (
        // Collapsed: Just the magnifying glass icon
        <Search
          size={20}
          color="white"
          strokeWidth={2.5}
          aria-label={t('search') || 'Search'}
        />
      ) : (
        <>
          {/* Expanded: Input field */}
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder={placeholder || t('search_tags') || 'Search by tags...'}
            aria-label={placeholder || t('search_tags') || 'Search by tags'}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#ffffff',
              fontSize: fontSize,
              fontWeight: '500',
              fontFamily: 'inherit'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />

          {/* Submit/Close button on right */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (searchTerm.trim()) {
                handleSubmit();
              } else {
                handleClose();
              }
            }}
            style={{
              position: 'absolute',
              right: '5px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            aria-label={searchTerm.trim() ? (t('submit_search') || 'Submit search') : (t('close_search') || 'Close search')}
          >
            <Search
              size={16}
              color="white"
              strokeWidth={2.5}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AnimatedSearchBar;
